import type { TimesheetSheetMeta } from "../hooks/use-timesheet-entries.ts";

export type TimesheetExtensionScenario =
  | "regular_with_attempts"
  | "regular_no_attempts"
  | "hr_review";

export interface TimesheetExtensionCopyContext {
  workDate: Date | string;
  attemptsRemaining: number;
}

const HR_REVIEW_EXTENSION_MESSAGE =
  "Your request will be sent to HR for review. Once approved, you will be able to fill in your timesheet for this date.";

function normalizeTimesheetType(type: string): string {
  return type.trim().toUpperCase();
}

export function shouldShowAttemptsRemainingBadge(type: string): boolean {
  const normalized = normalizeTimesheetType(type);
  return normalized !== "REMOTE" && normalized !== "CONSULTANT";
}

export function isHrReviewExtensionType(type: string): boolean {
  const normalized = normalizeTimesheetType(type);
  return (
    normalized === "WFH" ||
    normalized === "REMOTE" ||
    normalized === "CONSULTANT"
  );
}

function formatExtensionWorkDate(workDate: Date | string): string {
  const parsed =
    workDate instanceof Date ? workDate : new Date(`${String(workDate)}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return "this date";
  }

  return parsed.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function getTimesheetExtensionScenario(
  meta: Pick<TimesheetSheetMeta, "type" | "attemptsRemaining">,
): TimesheetExtensionScenario {
  if (isHrReviewExtensionType(meta.type)) {
    return "hr_review";
  }
  return meta.attemptsRemaining > 0
    ? "regular_with_attempts"
    : "regular_no_attempts";
}

export interface TimesheetExtensionCopy {
  title: string;
  messageLead: string;
  messageDate: string;
  messagePunctuation: "?" | ".";
  subtext?: string;
}

export function getTimesheetExtensionCopy(
  scenario: TimesheetExtensionScenario,
  context: TimesheetExtensionCopyContext,
): TimesheetExtensionCopy {
  const formattedDate = formatExtensionWorkDate(context.workDate);
  const attemptsRemaining = context.attemptsRemaining;
  const messageLead = "Do you want to request an extension for";

  switch (scenario) {
    case "regular_with_attempts":
      return {
        title: "Auto-Extend Timesheet",
        messageLead,
        messageDate: formattedDate,
        messagePunctuation: "?",
        subtext: `You have ${attemptsRemaining} attempt${attemptsRemaining === 1 ? "" : "s"} remaining. One attempt will be deducted from your balance.`,
      };
    case "regular_no_attempts":
      return {
        title: "Request Extension from HR",
        messageLead,
        messageDate: formattedDate,
        messagePunctuation: "?",
        subtext: HR_REVIEW_EXTENSION_MESSAGE,
      };
    case "hr_review":
      return {
        title: "Request Timesheet Extension",
        messageLead,
        messageDate: formattedDate,
        messagePunctuation: ".",
        subtext: HR_REVIEW_EXTENSION_MESSAGE,
      };
  }
}
